import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box, Chip, Divider, Drawer, IconButton, InputAdornment, Stack, TextField, Typography,
  alpha, useTheme,
} from '@mui/material';

import Iconify from '../Iconify';
import Scrollbar from '../Scrollbar';
import useAuth from '../../hooks/useAuth';
import { useAdminOnboarding } from '../../contexts/AdminOnboardingContext';
import { accessibleGuides, searchGuides } from '../../onboarding/guides';
import { GUIDE_STATUS, isGuideDone, normalizeProgress } from '../../onboarding/progress';
import { resolveJourney } from '../../onboarding/state';

// ---------------------------------------------------------------------------
// "Show me how" - every guide this user can run, in one searchable panel.
//
// Reachable forever from the help menu, which is the point: onboarding is not a
// thing that happens once in week one. Someone who has run a space for a year
// and needs to add a second billing centre opens this and gets walked through
// it, same as a brand-new admin.
//
// Two lists, deliberately: the journey the backend built for this persona, in
// its order, and then everything else the user has permission to run. The
// second list is why an owner can still take the front-desk guides.
// ---------------------------------------------------------------------------

const WIDTH = 400;

// Started but not finished: the row says so, and clicking it picks up where the
// user stopped rather than restarting from step one.
function isResumable(progress) {
  return progress.status === GUIDE_STATUS.inProgress || progress.status === GUIDE_STATUS.paused;
}

function GuideRow({ guide, done, inProgress, onStart }) {
  const theme = useTheme();
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.5}
      onClick={() => onStart(guide)}
      sx={{
        px: 1.5, py: 1.25, borderRadius: 1.5, cursor: 'pointer',
        transition: 'background-color .15s',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Box
        sx={{
          width: 34, height: 34, borderRadius: 1.25, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: done
            ? alpha(theme.palette.success.main, 0.12)
            : alpha(theme.palette.primary.main, 0.1),
        }}
      >
        <Iconify
          icon={done ? 'mdi:check-bold' : 'mdi:gesture-tap'}
          sx={{ width: 18, height: 18, color: done ? 'success.main' : 'primary.main' }}
        />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Typography variant="subtitle2" noWrap>{guide.title}</Typography>
          {inProgress && (
            <Chip
              size="small"
              label="In progress"
              sx={{
                height: 18, fontSize: 10, fontWeight: 700,
                color: 'info.dark', bgcolor: alpha(theme.palette.info.main, 0.16),
              }}
            />
          )}
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {guide.summary}
        </Typography>
      </Box>

      {guide.estMinutes ? (
        <Typography variant="caption" color="text.disabled" sx={{ flexShrink: 0 }}>
          {guide.estMinutes} min
        </Typography>
      ) : null}
    </Stack>
  );
}

GuideRow.propTypes = {
  guide: PropTypes.object.isRequired,
  done: PropTypes.bool,
  inProgress: PropTypes.bool,
  onStart: PropTypes.func.isRequired,
};

function Section({ title, entries, onStart }) {
  if (!entries.length) return null;
  return (
    <Box sx={{ px: 1, pb: 1 }}>
      <Typography
        variant="overline"
        sx={{ display: 'block', px: 1.5, pt: 1.5, pb: 0.5, color: 'text.disabled' }}
      >
        {title}
      </Typography>
      {entries.map((entry) => (
        <GuideRow
          key={entry.guide.id}
          guide={entry.guide}
          done={entry.done}
          inProgress={isResumable(entry.progress)}
          onStart={onStart}
        />
      ))}
    </Box>
  );
}

Section.propTypes = {
  title: PropTypes.string.isRequired,
  entries: PropTypes.array.isRequired,
  onStart: PropTypes.func.isRequired,
};

export default function GuideLauncherDrawer({ open, onClose }) {
  const { user, hasPermission } = useAuth();
  const { journey, guidesProgress, personaLabel, startGuide } = useAdminOnboarding();
  const [query, setQuery] = useState('');

  // Same shape the dashboards use: the org's plan carries the feature slugs.
  const features = user?.orgPlan?.features;
  const hasFeature = useMemo(
    () => (slug) => !!features?.some((f) => f.slug === slug),
    [features]
  );

  const quick = useMemo(
    () => resolveJourney(journey?.quick, guidesProgress),
    [journey, guidesProgress]
  );
  const setup = useMemo(
    () => resolveJourney(journey?.advanced, guidesProgress),
    [journey, guidesProgress]
  );

  // Everything the user could run that their journey did not already list.
  const more = useMemo(() => {
    const inJourney = new Set([...quick, ...setup].map((e) => e.guide.id));
    return accessibleGuides({ hasPermission, hasFeature })
      .filter((g) => !inJourney.has(g.id))
      .map((guide) => {
        const progress = normalizeProgress(guidesProgress?.[guide.id]);
        return { guide, progress, done: isGuideDone(progress) };
      });
  }, [quick, setup, guidesProgress, hasPermission, hasFeature]);

  // Search runs across all three lists at once: when someone types "invoice"
  // they want the guide, not the section it happens to sit in.
  const results = useMemo(() => {
    if (!query.trim()) return null;
    const all = [...quick, ...setup, ...more];
    const byId = new Map(all.map((e) => [e.guide.id, e]));
    return searchGuides(query, all.map((e) => e.guide)).map((g) => byId.get(g.id));
  }, [query, quick, setup, more]);

  const handleStart = (guide) => {
    const progress = normalizeProgress(guidesProgress?.[guide.id]);
    onClose();
    startGuide(guide.id, isResumable(progress) ? progress.current_step || 0 : 0);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: WIDTH } } }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2.5, py: 2 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" fontWeight={800}>Show me how</Typography>
          <Typography variant="caption" color="text.secondary">
            {personaLabel ? `Guides for your role: ${personaLabel}` : 'Walk through any part of DeskOS'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} aria-label="Close">
          <Iconify icon="eva:close-fill" />
        </IconButton>
      </Stack>

      <Box sx={{ px: 2.5, pb: 1.5 }}>
        <TextField
          fullWidth
          size="small"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search guides"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Iconify icon="eva:search-fill" sx={{ width: 18, height: 18, color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Divider />

      <Scrollbar sx={{ flex: 1 }}>
        {results ? (
          <>
            <Section title={`${results.length} result${results.length === 1 ? '' : 's'}`} entries={results} onStart={handleStart} />
            {!results.length && (
              <Typography variant="body2" color="text.secondary" sx={{ px: 3, py: 4, textAlign: 'center' }}>
                No guide matches that yet. Ask Dex in the chat and it can walk you through it.
              </Typography>
            )}
          </>
        ) : (
          <>
            <Section title="Start here" entries={quick} onStart={handleStart} />
            <Section title="Set up your space" entries={setup} onStart={handleStart} />
            <Section title="More guides" entries={more} onStart={handleStart} />
            {!quick.length && !setup.length && !more.length && (
              <Typography variant="body2" color="text.secondary" sx={{ px: 3, py: 4, textAlign: 'center' }}>
                No guides are available for your role yet.
              </Typography>
            )}
          </>
        )}
      </Scrollbar>
    </Drawer>
  );
}

GuideLauncherDrawer.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
};
